import { useState, useEffect, forwardRef } from "react";
import styled from "@emotion/styled";

import FormHelper from "../../utils/FormHelper";
import GridContainer from "../Molecules/GridContainer";
import GridItem from "../Molecules/GridItem";
import FormInput from "../Molecules/FormInput";
import FormSelect from "../Molecules/FormSelect";
import FormMultiSelect from "../Molecules/MultiSelect";
import FormImage from "../Molecules/FormImage";
import Checkbox from "../Molecules/Checkbox";
import TextArea from "../Molecules/TextArea";
import FormContent from "../Molecules/FormContent";
import Radio from "../Molecules/Radio";
import Button from "../Molecules/ButtonLogin";

const Form = forwardRef((props, ref) => {
  const {
    dataHelper,
    navigate,
    resetAfterSend,
    isLoadingData,
    afterInputs,
    additionalValidation,
    setTags,
    tags,
    disabled,
    config,
  } = props;

  const [aInputs, setAInputs] = useState([]);
  const [hasSetFocus, setHasSetFocus] = useState(false);
  const [isInputLoading, setIsInputLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  useEffect(() => {
    let thisIsInputLoading = false;
    setAInputs(
      props.inputs &&
        Object.entries(props.inputs).map(([key, input]) => {
          if (input.loading) {
            thisIsInputLoading = true;
          }

          if (!hasSetFocus && !input.getValue()) {
            setHasSetFocus(true);
            input.setFocus();
          }

          if (
            input.type === "hidden" ||
            (props.mode && input.hideOn?.includes(props.mode))
          ) {
            input.setIsVisible(false);
          } else {
            input.setIsVisible(true);
          }

          return {
            id: key,
            ...input,
          };
        })
    );

    setIsInputLoading(thisIsInputLoading);
  }, [props.inputs]);

  useEffect(() => {
    if (!isLoadingData) {
      validateForm();
    }
  }, [isLoadingData]);

  useEffect(() => {
    setIsFormLoading(props.loading || isInputLoading);
  }, [props.loading, isInputLoading]);

  const validationFcs = {
    "required": (formInput, validation) => {
      if (formInput.type === "checkbox") {
        if (!parseInt(formInput.getValue())) {
          formInput.focusError(validation.errorMessage);
          return false;
        }
      } else if (formInput.type === "image") {
        if (!formInput.getValue().length || formInput.getValue() === "[]") {
          formInput.focusError(validation.errorMessage);
          return false;
        }
      } else {
        if (!formInput.getValue().length) {
          formInput.focusError(validation.errorMessage);
          return false;
        }
      }

      return true;
    },
    "length": (formInput, validation) => {
      let value = formInput.getValue();
      let length = value.length;

      if (!validation.minLength && !validation.maxLength) {
        console.error("-- undefined minLength/maxLength!");
        return true;
      }

      if (
        (validation.minLength && length < validation.minLength) ||
        (validation.maxLength && length > validation.maxLength)
      ) {
        formInput.focusError(validation.errorMessage);
        return false;
      }

      return true;
    },
    "passwordTips": (formInput, validation) => {
      if (!FormHelper.validatePasswordTips(formInput)) {
        formInput.focusError(validation.errorMessage);
        return false;
      }
      return true;
    },
    "age": (formInput, validation) => {
      let value = formInput.getValue();
      if (
        value?.length &&
        !FormHelper.validateAge({ ...validation.config, date: value })
      ) {
        formInput.focusError(validation.errorMessage);
        return false;
      }

      return true;
    },
    "cpf": (formInput, validation) => {
      const valueInput = formInput.getValue().replace(/\D/g, "");
 
      if (valueInput?.length === 0) return true

      if (valueInput.length === 11) {
        if (!FormHelper.validateCPF(valueInput)) {
          formInput.focusError(validation.errorMessage);
          return false;
        }
        return true;
      }

      formInput.focusError("Digite um CPF válido");
      return false;
    },
    "email": (formInput, validation) => {
      const email = formInput.getValue();
      if (email?.length && !FormHelper.validateEmail(email)) {
        formInput.focusError(validation.errorMessage);
        return false;
      }

      return true;
    },
    "custom": async (formInput, validation, aInputs) => {
      let isValid = await validation.customFunction(
        formInput,
        validation,
        aInputs
      );

      if (!isValid) {
        formInput.focusError(validation.errorMessage);
        return false;
      }

      return true;
    },
    "personName": (formInput, validation) => {
      const valueInput = formInput.getValue();
      if (!FormHelper.validatePersonName(valueInput)) {
        formInput.focusError(validation.errorMessage);
        return false;
      }
      return true;
    },
    "phone": (formInput, validation) => {
      const valueInput = formInput.getValue() || "";

      if (!FormHelper.validateCellphone(valueInput)) {
        formInput.focusError(validation.errorMessage);
        return false;
      }
      return true;
    },
    "username": (formInput, validation) => {
      const username = formInput.getValue();
      if (username?.length && !FormHelper.validateUsername(username)) {
        formInput.focusError(validation.errorMessage);
        return false;
      }

      return true;
    },
  };

  const checkSend = (e) => {
    if (!isFormLoading) {
      var keyCode = e.which || e.keyCode;
      if (keyCode === 13)
        // enter
        submit();
    }
  };

  const clearValidation = () => {
    Object.entries(props.inputs).map(([key, input]) => {
      input.setError("");
    });
  };

  const validateForm = async () => {
    let isValid = true;
    // reverse - valida de baixo para cima, assim, o último input a ser validado será o primeiro,
    //           fazendo com que o cursor termine no primeiro input inválido.
    let validateInputs = [...aInputs].reverse();

    // É necessário usar loop nativo para que 'await' seja respeitado
    for (let i = 0; i < validateInputs.length; i++) {
      let input = validateInputs[i];

      if (input.isVisible && input.validations?.length) {
        for (let j = 0; j < input.validations.length; j++) {
          let validation = input.validations[j];

          let inputValid = true;

          if (validationFcs[validation.type]) {
            inputValid = await validationFcs[validation.type](
              input,
              validation,
              aInputs
            );
          }

          if (inputValid) {
            continue;
          } else {
            isValid = false;
            break;
          }
        }
      }
    }

    if (additionalValidation) {
      isValid = additionalValidation(isValid);
    }

    return isValid;
  };

  const resetForm = () => {
    Object.entries(props.inputs).map(([key, input]) => {
      input?.reset("");
    });
  };

  const submit = async (e, addAnother = false) => {
    // Por via das dúvidas, se vier de submit, recusa. Nós mesmos realizamos o submit.
    if (e?.type === "submit") {
      e.preventDefault();
      return false;
    }

    if (isFormLoading) return false;

    props.setLoading(1);

    clearValidation();

    if (await validateForm()) {
      let formData = {};
      Object.entries(props.inputs).map(([key, input]) => {
        formData[key] = input.getValue();
      });

      const finalFormData = {
        ...formData,
        LoyaltyStreet__c: `${
          formData?.LoyaltyStreet__c?.trim()?.replaceAll(",", "") || ""
        }, ${formData?.Complement?.trim()?.replaceAll(",", "") || ""}, ${
          formData?.Neighborhood?.trim()?.replaceAll(",", "") || ""
        }`,
      };

      const result = await props.handleSend(finalFormData, addAnother);

      props.setLoading(0);
      if (result?.redirect) {
        if (typeof result.redirect === "string") {
          window.location.href = result.redirect;
        } else if (typeof result.redirect === "object") {
          navigate(...result.redirect);
        } else if (result.redirect === true) {
          navigate(0);
        }
      }
      if (result?.success !== undefined) {
        if (result.success) {
          resetForm();
          props.setLoading(0);
        }
      } else if (addAnother || resetAfterSend) {
        resetForm();
      }
    } else {
      props.setLoading(0);
    }
  };

  return (
    <StyledForm ref={ref} onSubmit={submit} onKeyDown={checkSend}>
      <GridContainer
        {...props.gridConfig}
        listSpacing={0}
        columnGutter={props.layout === "compressed" ? undefined : 35}
      >
        {aInputs.map((input, index) => {
          if (props.mode && input.hideOn?.includes(props.mode)) {
            return false;
          }

          const inputProps = {
            input,
            dataHelper,
            ref: input.ref,
            config,
          };

          let inputElement;
          switch (input.type) {
            case "checkbox":
              inputElement = <Checkbox {...inputProps} />;
              break;
            case "radio":
              inputElement = <Radio {...inputProps} />;
              break;
            case "textarea":
              inputElement = <TextArea {...inputProps} />;
              break;
            case "multi":
              inputElement = (
                <FormMultiSelect
                  {...inputProps}
                  setTags={setTags}
                  tags={tags}
                />
              );
              break;
            case "select":
              inputElement = <FormSelect {...inputProps} />;
              break;
            case "image":
              inputElement = <FormImage {...inputProps} />;
              break;
            case "content":
              inputElement = <FormContent {...inputProps} />;
              break;
            case "text":
            default:
              inputElement = <FormInput {...inputProps} />;
          }

          return (
            <GridItem
              {...props.gridConfig}
              key={index}
              {...input.size}
              className={input.type === "hidden" ? "hide" : "show"}
            >
              {inputElement}
            </GridItem>
          );
        })}
        {afterInputs}
      </GridContainer>
      <div className={"button-container"}>
        <Button onClick={submit} disabled={isFormLoading || disabled}>
          {props.saveLabel || "continue"}
        </Button>
        {props.asideSend || props.buttonSendAndAnother ? (
          <div className={"side-button"}>
            {props.asideSend ||
              (props.buttonSendAndAnother && (
                <Button
                  onClick={(e) => submit(e, true)}
                  disabled={isFormLoading}
                >
                  Enviar e cadastrar outro
                </Button>
              ))}
          </div>
        ) : null}
      </div>
    </StyledForm>
  );
});

export default Form;

/* style */

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: "relative";

  .form-message {
    margin-top: 20px;
    align-self: start;

    &.error {
      color: #c62828;
    }

    &.success {
      color: #558b2f;
    }
  }

  .header-user-icon {
    text-align: center;
    width: 100px;
    height: 100px;
    margin-bottom: 10px;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    margin-top: 25px;
    align-items: center;
    width: 100%;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: center;

    a {
      align-self: center;
    }

    .side-button {
      flex: 1;
      text-align: left;
    }
  }
`;
